// How a number is written wherever a person reads it: at most two decimal places,
// trailing zeros dropped, whole numbers left whole. 12 stays 12, 3.5 stays 3.5,
// 3.4712 reads 3.47 and 96.0000000000000000 reads 96. One rule for the whole console,
// so a value reads the same on a chart, a widget and in chat.
//
// Display only. A CSV export is the place to get every digit back, so nothing on an
// export path calls into this file.

// Anything that rounds to zero is written as 0. Without this -0.001 shows as "-0",
// which reads as a sign error rather than as "nothing".
const ROUNDS_TO_ZERO = 0.005;

// A number, or a string holding one, as display text: locale separators (unless
// `useGrouping` is false) and at most two decimals. Null when there is no finite
// number to show, so each caller picks its own placeholder.
export function formatDisplayNumber(value, locale, { useGrouping = true } = {}) {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed)) return null;
  const n = Math.abs(parsed) < ROUNDS_TO_ZERO ? 0 : parsed;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, useGrouping }).format(n);
}

const LONG_DECIMAL = /^(-?)(\d+)\.(\d{3,})$/;

// A plain decimal written with more than two places, rounded to two on its digits:
// "3.4712" → "3.47", "96.0000000000000000" → "96", "99.995" → "100", "-0.001" → "0".
// Null for anything else, which callers show as written: an integer-shaped code (an
// MRN, a zip, an ID of any length), a value already at two places or fewer, text,
// a version string like "1.2.3". Callers decide by type whether a value is a number
// at all; see capDisplayDecimals.
//
// Rounded on the written digits, half away from zero, rather than through a JS
// number. A string is an exact decimal and a double is not: Number("1.005") is
// 1.00499…, which would round down, and an integer part past 2^53 would come back
// with different digits.
export function roundDecimalString(raw) {
  const m = LONG_DECIMAL.exec(String(raw).trim());
  if (!m) return null;
  const [, sign, whole, frac] = m;
  const digits = (whole + frac.slice(0, 2)).split('').map(Number);
  if (Number(frac[2]) >= 5) {
    let i = digits.length - 1;
    while (i >= 0 && digits[i] === 9) {
      digits[i] = 0;
      i -= 1;
    }
    if (i < 0) digits.unshift(1);
    else digits[i] += 1;
  }
  const kept = digits.join('');
  const intPart = kept.slice(0, -2);
  const fracPart = kept.slice(-2).replace(/0+$/, '');
  const isZero = /^0+$/.test(intPart) && fracPart === '';
  return `${isZero ? '' : sign}${intPart}${fracPart ? `.${fracPart}` : ''}`;
}

// For a value KNOWN to be a number — by its type, or because the server marked its
// column as a computed decimal — on a surface that otherwise shows values as written,
// with no separators: only a decimal carrying more than two places changes. Integers
// and anything non-numeric come back exactly as String() would write them.
//
// Never apply this to a value by its shape alone. A figure quoted from a document
// ("0.125 mg") arrives as a plain string and must stay exactly as written, and
// nothing in "0.125" says whether it was computed or quoted.
export function capDisplayDecimals(value) {
  const s = String(value);
  const rounded = roundDecimalString(s);
  if (rounded != null) return rounded;
  // A small JS number stringifies in exponent form (1e-7), which the pattern above
  // does not read.
  if (typeof value === 'number' && s.includes('e-')) {
    return formatDisplayNumber(value, 'en-US', { useGrouping: false });
  }
  return s;
}
